'use client';

import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import PlusIcon from '@app/components/icons/PlusIcon';
import { cn } from '@libs/utils';
import { getZIndexClass, Z_INDEX } from '@libs/zIndex';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  position?: 'center' | 'top' | 'bottom';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  closeButtonClassName?: string;
  backdropClassName?: string;
  animation?: 'fade' | 'slide-up' | 'slide-down' | 'zoom' | 'none';
  zIndex?: number;
  closeOnTitle?: boolean;
  headerActions?: ReactNode;
}

export interface ModalContextType {
  modals: Record<string, ModalState>;
  openModal: (id: string, config?: Partial<ModalConfig>) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  isModalOpen: (id: string) => boolean;
  updateModal: (id: string, config: Partial<ModalConfig>) => void;
}

export interface ModalState {
  isOpen: boolean;
  config: ModalConfig;
}

export interface ModalConfig {
  title?: string;
  subtitle?: string;
  size: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  position: 'center' | 'top' | 'bottom';
  showCloseButton: boolean;
  closeOnBackdropClick: boolean;
  closeOnEscape: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  animation: 'fade' | 'slide-up' | 'slide-down' | 'zoom' | 'none';
  zIndex: number;
  persistent?: boolean;
}

export type ModalID = string;

export interface ModalManagerProps {
  children: ReactNode;
}

export interface ModalContentProps {
  modalId: string;
  children: ReactNode;
}

export default function Modal({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  size = 'md',
  position = 'center',
  showCloseButton = true,
  closeOnTitle = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className,
  overlayClassName,
  backdropClassName,
  closeButtonClassName,
  contentClassName,
  animation = 'fade',
  zIndex = Z_INDEX.modal,
  headerActions,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus management
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full mx-4',
  };

  // Position classes
  const positionClasses = {
    center: 'items-center justify-center',
    top: 'items-start justify-center pt-20',
    bottom: 'items-end justify-center pb-20',
  };

  // Animation classes
  const animationClasses = {
    fade: isOpen ? 'animate-in fade-in duration-200' : 'animate-out fade-out duration-200',
    'slide-up': isOpen
      ? 'animate-in slide-in-from-bottom-4 fade-in duration-200'
      : 'animate-out slide-out-to-bottom-4 fade-out duration-200',
    'slide-down': isOpen
      ? 'animate-in slide-in-from-top-4 fade-in duration-200'
      : 'animate-out slide-out-to-top-4 fade-out duration-200',
    zoom: isOpen
      ? 'animate-in zoom-in-95 fade-in duration-200'
      : 'animate-out zoom-out-95 fade-out duration-200',
    none: '',
  };

  const modalContent = (
    <div
      className={cn('fixed inset-0 flex p-4', positionClasses[position], overlayClassName)}
      style={{ zIndex }}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 backdrop-blur-sm',
          backdropClassName,
          !backdropClassName?.match('bg-') && 'bg-gray-900/80',
          animation !== 'none' &&
            (isOpen ? 'animate-in fade-in duration-200' : 'animate-out fade-out duration-200')
        )}
        style={{ zIndex: zIndex - 1 }}
      />

      {/* Close Button - Fixed ở góc trên bên phải */}
      {showCloseButton && ((title && !closeOnTitle) || !title) && (
        <button
          onClick={onClose}
          className={cn(
            'absolute top-4 right-4 w-10 h-10',
            'text-gray-900',
            'hover:text-gray-300',
            'cursor-pointer',
            'flex items-center justify-center',
            'transition-all duration-200 ease-in-out',
            'hover:scale-105 active:scale-95',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            animation !== 'none' &&
              (isOpen
                ? 'animate-in zoom-in-95 fade-in duration-200 delay-100'
                : 'animate-out zoom-out-95 fade-out duration-200'),
            closeButtonClassName
          )}
          style={{ zIndex: zIndex + 1 }}
          aria-label="Close modal"
        >
          <PlusIcon className="w-5 h-5 rotate-45" />
        </button>
      )}

      {/* Modal Content */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className={cn(
          'relative rounded-sm shadow-xl shape-top shape-bottom outline-none border border-gray-100 rounded-xl bg-white',
          animation !== 'none' && animationClasses[animation],
          contentClassName,
          !contentClassName?.match(/(^| )w-/) && 'w-full',
          !contentClassName?.match(/(^| )max-w-/) && sizeClasses[size],
          className,
          'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent'
        )}
        style={{ zIndex }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby={subtitle ? 'modal-subtitle' : undefined}
      >
        <div className="card-with-shapes h-full flex flex-col gap-6">
          {/* Header - Chỉ hiển thị khi có title hoặc subtitle */}
          {(title || subtitle) && (
            <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-1 rounded-t-xl flex items-center justify-start gap-5">
              <div className="flex flex-col justify-center grow">
                {title && (
                  <h2 id="modal-title" className="text-xl font-semibold text-gray-900">
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p id="modal-subtitle" className="mt-1 text-sm text-gray-600">
                    {subtitle}
                  </p>
                )}
              </div>
              {headerActions && (
                <div className="flex items-center justify-center gap-4">{headerActions}</div>
              )}
              {closeOnTitle && (
                <button
                  onClick={onClose}
                  className={cn(
                    'right-4 w-5 h-5',
                    'text-gray-900',
                    'hover:text-gray-300',
                    'cursor-pointer',
                    'flex items-center justify-center',
                    'transition-all duration-200 ease-in-out',
                    'hover:scale-105 active:scale-95',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                    animation !== 'none' &&
                      (isOpen
                        ? 'animate-in zoom-in-95 fade-in duration-200 delay-100'
                        : 'animate-out zoom-out-95 fade-out duration-200'),
                    closeButtonClassName
                  )}
                  style={{ zIndex: zIndex + 1 }}
                  aria-label="Close modal"
                >
                  <PlusIcon className="w-5 h-5 rotate-45" />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className={cn('p-6 pt-0 max-h-full h-full flex flex-col overflow-hidden')}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  // Kiểm tra nếu đang chạy trên client side
  if (typeof window === 'undefined') return null;

  return createPortal(modalContent, document.body);
}
